/**
 * Maps OoTR region names to tracker zone codes.
 *
 * To update: add new regions from a newer Overworld.json.
 * Exits between regions in the SAME zone are ignored (intra-zone = free).
 */
export const REGION_TO_ZONE: Record<string, string> = {
  // ─── Special / Root ────────────────────────────────────────
  'Root': '_root',
  'Root Exits': '_root',
  'Child Spawn': 'Spawns',
  'Adult Spawn': 'Spawns',
  'Farores Wind Warp': '_farore',

  // ─── Warp Song destinations ────────────────────────────────
  'Prelude of Light Warp': 'MKT',
  'Minuet of Forest Warp': 'SFM',
  'Bolero of Fire Warp': 'DMC',
  'Serenade of Water Warp': 'LH',
  'Nocturne of Shadow Warp': 'GY',
  'Requiem of Spirit Warp': 'DCol',

  // ─── Kokiri Forest (KF) ───────────────────────────────────
  'Kokiri Forest': 'KF',
  'KF Outside Deku Tree': 'KF',
  'KF Links House': 'KF',
  'KF Midos House': 'KF',
  'KF Sarias House': 'KF',
  'KF House of Twins': 'KF',
  'KF Know It All House': 'KF',
  'KF Kokiri Shop': 'KF',
  'KF Storms Grotto': 'KF',

  // ─── Lost Woods (LW) ──────────────────────────────────────
  'Lost Woods': 'LW',
  'LW Beyond Mido': 'LW',
  'LW Forest Exit': 'LW',
  'LW Underwater Entrance': 'LW',
  'Lost Woods Mushroom Timeout': 'LW',
  'LW Near Shortcuts Grotto': 'LW',
  'Deku Theater': 'LW',
  'LW Scrubs Grotto': 'LW',

  // ─── Sacred Forest Meadow (SFM) ───────────────────────────
  'SFM Entryway': 'SFM',
  'Sacred Forest Meadow': 'SFM',
  'SFM Forest Temple Entrance Ledge': 'SFM',
  'SFM Fairy Grotto': 'SFM',
  'SFM Storms Grotto': 'SFM',
  'SFM Wolfos Grotto': 'SFM',

  // ─── LW Bridge ────────────────────────────────────────────
  'LW Bridge From Forest': 'LW Bridge',
  'LW Bridge': 'LW Bridge',

  // ─── Hyrule Field (HF) ────────────────────────────────────
  'Hyrule Field': 'HF',
  'HF Southeast Grotto': 'HF',
  'HF Open Grotto': 'HF',
  'HF Inside Fence Grotto': 'HF',
  'HF Cow Grotto': 'HF',
  'HF Near Market Grotto': 'HF',
  'HF Fairy Grotto': 'HF',
  'HF Near Kak Grotto': 'HF',
  'HF Tektite Grotto': 'HF',

  // ─── Market (MKT) ─────────────────────────────────────────
  'Market Entrance': 'MKT',
  'Market': 'MKT',
  'Market Back Alley': 'MKT',
  'Market Adult ReDeads': 'MKT',
  'ToT Entrance': 'MKT',
  'Temple of Time': 'MKT',
  'Beyond Door of Time': 'MKT',
  'Beyond Door of Time Skippable Locations': 'MKT',
  'Market Guard House': 'MKT Bridge',
  'Market Bazaar': 'MKT',
  'Market Mask Shop': 'MKT',
  'Market Mask Shop Storefront': 'MKT',
  'Market Shooting Gallery': 'MKT',
  'Market Bombchu Bowling': 'MKT',
  'Market Potion Shop': 'MKT',
  'Market Treasure Chest Game': 'MKT',
  'Market Bombchu Shop': 'MKT',
  'Market Dog Lady House': 'MKT',
  'Market Man in Green House': 'MKT',
  'MKT Bridge': 'MKT Bridge',

  // ─── Hyrule Castle / OGC (HC, OGC) ────────────────────────
  'Castle Grounds': 'HC',
  'Hyrule Castle Grounds': 'HC',
  'HC Garden': 'HC',
  'HC Garden Skippable Locations': 'HC',
  'HC Great Fairy Fountain': 'HC',
  'HC Storms Grotto': 'HC',
  'Castle Grounds From Ganons Castle': 'OGC',
  'Ganons Castle Grounds': 'OGC',
  'Ganons Castle Ledge': 'OGC',
  'OGC Great Fairy Fountain': 'OGC',

  // ─── Lon Lon Ranch (LLR) ──────────────────────────────────
  'Lon Lon Ranch': 'LLR',
  'LLR Talons House': 'LLR',
  'LLR Stables': 'LLR',
  'LLR Tower': 'LLR',
  'LLR Grotto': 'LLR',

  // ─── Kakariko (KAK) ───────────────────────────────────────
  'Kakariko Village': 'KAK',
  'Kak Impas Ledge': 'KAK',
  'Kak Impas Rooftop': 'KAK',
  'Kak Odd Medicine Rooftop': 'KAK',
  'Kak Backyard': 'KAK',
  'Kak Behind Gate': 'KAK',
  'Kak Carpenter Boss House': 'KAK',
  'Kak House of Skulltula': 'KAK',
  'Kak Impas House': 'KAK',
  'Kak Impas House Back': 'KAK',
  'Kak Impas House Near Cow': 'KAK',
  'Kak Windmill': 'KAK',
  'Kak Bazaar': 'KAK',
  'Kak Shooting Gallery': 'KAK',
  'Kak Potion Shop Front': 'KAK',
  'Kak Potion Shop Back': 'KAK',
  'Kak Odd Medicine Building': 'KAK',
  'Kak Open Grotto': 'KAK',
  'Kak ReDead Grotto': 'KAK',

  // ─── Graveyard (GY) ───────────────────────────────────────
  'Graveyard': 'GY',
  'Graveyard Shield Grave': 'GY',
  'Graveyard Heart Piece Grave': 'GY',
  'Graveyard Royal Familys Tomb': 'GY',
  'Graveyard Royal Familys Inner Sanctum': 'GY',
  'Graveyard Dampes Grave': 'GY',
  'Graveyard Dampes House': 'GY',
  'Graveyard Warp Pad Region': 'GY',

  // ─── Death Mountain Trail (DMT) ───────────────────────────
  'Death Mountain': 'DMT',
  'Death Mountain Summit': 'DMT',
  'DMT Owl Flight': 'DMT',
  'DMT Great Fairy Fountain': 'DMT',
  'DMT Storms Grotto': 'DMT',
  'DMT Cow Grotto': 'DMT',

  // ─── Goron City (GC) ──────────────────────────────────────
  'Goron City': 'GC',
  'GC Boulder Maze Start': 'GC',
  'GC Boulder Maze Upper 1': 'GC',
  'GC Boulder Maze Upper 2': 'GC',
  'GC Boulder Maze Upper 3': 'GC',
  'GC Boulder Maze Lower 1': 'GC',
  'GC Boulder Maze Lower 2': 'GC',
  'GC Boulder Maze Lower 3': 'GC',
  'GC Boulder Maze Lower 4': 'GC',
  'GC Boulder Maze Lower 5': 'GC',
  'GC Boulder Maze Central': 'GC',
  'GC Boulder Maze Left Chest': 'GC',
  'GC Boulder Maze 2 Chests': 'GC',
  'GC Woods Warp': 'GC',
  'GC Darunias Chamber': 'GC',
  'GC Grotto Platform': 'GC',
  'GC Spinning Pot': 'GC',
  'GC Shop': 'GC',
  'GC Grotto': 'GC',

  // ─── Death Mountain Crater (DMC) ──────────────────────────
  'DMC Upper Nearby': 'DMC',
  'DMC Upper Local': 'DMC',
  'DMC Pierre Platform': 'DMC',
  'DMC Ladder Area Nearby': 'DMC',
  'DMC Lower Nearby': 'DMC',
  'DMC Lower Local': 'DMC',
  'DMC Central Nearby': 'DMC',
  'DMC Central Local': 'DMC',
  'DMC Fire Temple Entrance': 'DMC',
  'DMC Great Fairy Fountain': 'DMC',
  'DMC Upper Grotto': 'DMC',
  'DMC Hammer Grotto': 'DMC',

  // ─── Zora River (ZR) ──────────────────────────────────────
  'ZR Front': 'ZR',
  'Zora River': 'ZR',
  'ZR Behind Waterfall': 'ZR',
  'ZR Top of Waterfall': 'ZR',
  'ZR Open Grotto': 'ZR',
  'ZR Fairy Grotto': 'ZR',
  'ZR Storms Grotto': 'ZR',

  // ─── Zora's Domain (ZD) ───────────────────────────────────
  'Zoras Domain': 'ZD',
  'ZD Behind King Zora': 'ZD',
  'ZD Eyeball Frog Timeout': 'ZD',
  'ZD Shop': 'ZD',
  'ZD Storms Grotto': 'ZD',

  // ─── Zora's Fountain (ZF) ─────────────────────────────────
  'Zoras Fountain': 'ZF',
  'ZF Underwater': 'ZF',
  'ZF Hidden Cave Adult': 'ZF',
  'ZF Ice Ledge': 'ZF',
  'ZF Great Fairy Fountain': 'ZF',

  // ─── Lake Hylia (LH) ──────────────────────────────────────
  'Lake Hylia': 'LH',
  'LH Fishing Island': 'LH',
  'LH Owl Flight': 'LH',
  'LH Lab': 'LH',
  'LH Fishing Hole': 'LH',
  'LH Grotto': 'LH',

  // ─── Gerudo Valley (GV) ───────────────────────────────────
  'Gerudo Valley': 'GV',
  'GV Upper Stream': 'GV',
  'GV Lower Stream': 'GV',
  'GV Grotto Ledge': 'GV',
  'GV Crate Ledge': 'GV',
  'GV Fortress Side': 'GV',
  'GV Carpenter Tent': 'GV',
  'GV Octorok Grotto': 'GV',
  'GV Storms Grotto': 'GV',

  // ─── Gerudo Fortress (GF) ─────────────────────────────────
  'Gerudo Fortress': 'GF',
  'GF Entrances Behind Crates': 'GF',
  'Gerudo Fortress Archery Range': 'GF',
  'GF Roof Entrance Cluster': 'GF',
  'GF Kitchen Roof Access': 'GF',
  'GF 3 Torches Jail Exterior': 'GF',
  'GF Chest Roof': 'GF',
  'GF Roof Gold Skulltula': 'GF',
  'GF Break Room Entrance': 'GF',
  'GF Balcony': 'GF',
  'GF Above Jail Child Locations': 'GF',
  'GF Outside Gate': 'GF',
  'GF Storms Grotto': 'GF',
  // Hideout interior rooms -> GF
  'Hideout 1 Torch Jail': 'GF',
  'Hideout 2 Torches Jail': 'GF',
  'Hideout 3 Torches Jail': 'GF',
  'Hideout 4 Torches Jail': 'GF',
  'Hideout Kitchen Hallway': 'GF',
  'Hideout Kitchen Front': 'GF',
  'Hideout Kitchen Rear': 'GF',
  'Hideout Kitchen Pots': 'GF',
  'Hideout Break Room': 'GF',
  'Hideout Hall to Balcony': 'GF',

  // ─── Haunted Wasteland ────────────────────────────────────
  'Wasteland Near Fortress': 'Wasteland',
  'Haunted Wasteland': 'Wasteland',
  'Wasteland Near Crate': 'Wasteland',
  'Wasteland Near Colossus': 'Wasteland',

  // ─── Desert Colossus (DCol) ───────────────────────────────
  'Desert Colossus': 'DCol',
  'Desert Colossus From Spirit Lobby': 'DCol',
  'Desert Colossus Hands': 'DCol',
  'Colossus Great Fairy Fountain': 'DCol',
  'Colossus Grotto': 'DCol',
};
